import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { formatPhoneNumber, handlePhoneInput } from '../../utils/phoneFormat';

const ReservationSuccessModal = ({ isOpen, onClose, reservation }) => {
  const { user, updateUser } = useContext(AuthContext);
  const [emailConfirmation, setEmailConfirmation] = useState(true);
  const [textConfirmation, setTextConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationSent, setConfirmationSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [tempPhoneNumber, setTempPhoneNumber] = useState(''); // For editing
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [phoneSaveError, setPhoneSaveError] = useState('');
  const [savePhoneToProfile, setSavePhoneToProfile] = useState(true); // Default to saving

  // Initialize phone number from user data when modal opens
  useEffect(() => {
    if (isOpen && user) {
      console.log('=== MODAL OPENED ===');
      console.log('User data:', {
        phone: user.phone,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        smsReservationReminder: user.smsReservationReminder
      });
      const formattedPhone = user.phone ? formatPhoneNumber(user.phone) : '';
      console.log('Formatted phone for display:', formattedPhone);
      setPhoneNumber(formattedPhone);
      setTempPhoneNumber(formattedPhone);
      setIsEditingPhone(false); // Don't auto-open edit
      setSavePhoneToProfile(true); // Reset to default
      // Auto-check text confirmation if user has SMS reminders enabled and has a phone number
      setTextConfirmation(user.smsReservationReminder && !!user.phone);
      setConfirmationSent(false); // Reset confirmation sent
      setPhoneSaveError(''); // Clear any errors
      console.log('Modal state initialized', {
        autoCheckTextConfirmation: user.smsReservationReminder && !!user.phone
      });
    }
  }, [isOpen, user]);

  // Debug confirmationSent state changes
  useEffect(() => {
    console.log('*** confirmationSent state changed to:', confirmationSent);
  }, [confirmationSent]);

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const handlePhoneChange = (e) => {
    const formatted = handlePhoneInput(e.target.value);
    setTempPhoneNumber(formatted);
    setPhoneSaveError(''); // Clear errors as user types
  };

  const handleTextConfirmationChange = (checked) => {
    setTextConfirmation(checked);

    // If checking text confirmation and no phone number, show input
    if (checked && !phoneNumber && !isEditingPhone) {
      setIsEditingPhone(true);
      setTempPhoneNumber('');
    }
  };

  const handleSendConfirmation = async () => {
    console.log('=== handleSendConfirmation START ===', {
      textConfirmation,
      emailConfirmation,
      isEditingPhone,
      phoneNumber,
      tempPhoneNumber
    });
    setPhoneSaveError('');

    // If text confirmation is enabled, validate and potentially save phone number
    if (textConfirmation || isEditingPhone) {
      const phoneToUse = isEditingPhone ? tempPhoneNumber : phoneNumber;
      const digitsOnly = phoneToUse.replace(/\D/g, '');

      console.log('Phone validation', { phoneToUse, digitsOnly, textConfirmation });

      // Validate phone number if text confirmation is requested
      if (textConfirmation && (!digitsOnly || digitsOnly.length !== 10)) {
        console.log('Phone validation FAILED - invalid phone number');
        setPhoneSaveError('Please enter a valid 10-digit phone number');
        return;
      }

      // Save to profile if checkbox is checked and phone has been edited
      if (isEditingPhone && savePhoneToProfile && digitsOnly) {
        console.log('Attempting to save phone to profile...', { digitsOnly });
        try {
          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
          const response = await fetch(`${API_URL}/api/users/profile`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              phone: digitsOnly
            })
          });

          if (!response.ok) {
            console.error('Phone save response not OK:', response.status);
            throw new Error('Failed to save phone number');
          }

          console.log('Phone saved successfully to profile');
          // Update user context
          await updateUser();
          console.log('User context updated after phone save');
        } catch (error) {
          console.error('Error saving phone:', error);
          setPhoneSaveError('Failed to save phone number. Please try again.');
          return;
        }
      }

      // Apply the temp phone number for this session
      if (isEditingPhone) {
        console.log('Applying temp phone number to phoneNumber state');
        setPhoneNumber(tempPhoneNumber);
        setIsEditingPhone(false);
      }
    }

    if (!emailConfirmation && !textConfirmation) {
      console.log('No confirmations selected - showing success and closing');
      setConfirmationSent(true);
      setTimeout(() => {
        handleClose();
      }, 3500);
      return;
    }

    console.log('Sending confirmations - starting simulation...');
    setIsSubmitting(true);
    try {
      // In a real app, this would call an API to send confirmations
      // For now, we'll simulate the API call
      console.log('Simulating API call with 1.5s delay...');
      await new Promise(resolve => setTimeout(resolve, 1500));

      console.log('API simulation complete - setting confirmationSent to TRUE');
      setConfirmationSent(true);
      console.log('confirmationSent state set, should trigger UI update');

      // Auto-close after showing success message
      console.log('Starting auto-close timer (3.5s)');
      setTimeout(() => {
        console.log('Auto-close timer fired - calling handleClose');
        handleClose();
      }, 3500);
    } catch (error) {
      console.error('Error sending confirmation:', error);
      // For demo purposes, we'll still show success
      setConfirmationSent(true);
      setTimeout(() => {
        handleClose();
      }, 3500);
    } finally {
      setIsSubmitting(false);
      console.log('=== handleSendConfirmation END ===');
    }
  };

  const handleClose = () => {
    setEmailConfirmation(true);
    setTextConfirmation(false);
    setConfirmationSent(false);
    setIsSubmitting(false);
    onClose();
  };

  if (!reservation) return null;

  const startDateTime = formatDateTime(reservation.start_time);
  const endDateTime = formatDateTime(reservation.end_time);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Reservation Confirmed!"
      size="medium"
      footer={
        confirmationSent ? (
          <div className="space-x-3">
            <Button onClick={handleClose} variant="primary">
              Done
            </Button>
          </div>
        ) : (
          <div className="space-x-3">
            <Button onClick={handleClose} variant="outline">
              Skip Confirmation
            </Button>
            <Button
              onClick={handleSendConfirmation}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting 
                ? 'Sending...' 
                : (!emailConfirmation && !textConfirmation) 
                  ? 'Done' 
                  : 'Send Confirmation'
              }
            </Button>
          </div>
        )
      }
    >
      {confirmationSent ? (
        // Confirmation sent success
        <div className="text-center py-6">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-teal-100 border-2 border-teal-500 shadow-retro mb-4">
            <svg className="h-8 w-8 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">
            {(emailConfirmation || textConfirmation) ? 'Confirmation Sent!' : 'Reservation Complete!'}
          </h3>
          <p className="text-sm text-slate-600">
            {emailConfirmation && textConfirmation && 'Email and text confirmations have been sent.'}
            {emailConfirmation && !textConfirmation && 'Email confirmation has been sent.'}
            {!emailConfirmation && textConfirmation && 'Text confirmation has been sent.'}
            {!emailConfirmation && !textConfirmation && 'Your reservation has been successfully created.'}
          </p>
        </div>
      ) : (
        // Reservation success details
        <div className="space-y-6">
          {/* Success Header */}
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-teal-100 border-2 border-teal-500 shadow-retro mb-4">
              <svg className="h-8 w-8 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Your reservation is confirmed!
            </h3>
            <p className="text-sm text-slate-600">
              Reservation #{reservation.id} has been successfully created.
            </p>
          </div>

          {/* Reservation Details */}
          <div className="bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 rounded-lg p-4 border-2 border-slate-900 shadow-retro">
            <div className="space-y-3">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-teal-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <div>
                  <p className="font-bold text-slate-900">{reservation.board_name}</p>
                  <p className="text-sm text-slate-700">{reservation.gym_name}</p>
                </div>
              </div>

              <div className="flex items-center">
                <svg className="h-5 w-5 text-purple-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="font-bold text-slate-900">{startDateTime.date}</p>
                  <p className="text-sm text-slate-700">{startDateTime.time} - {endDateTime.time}</p>
                </div>
              </div>

              {reservation.notes && (
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-magenta-600 mr-3 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                  <div>
                    <p className="font-bold text-slate-900">Notes</p>
                    <p className="text-sm text-slate-700">{reservation.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Check-in Reminder */}
          <div className="bg-sunshine-50 rounded-lg p-4 border-2 border-sunshine-500 shadow-retro">
            <div className="flex">
              <svg className="h-5 w-5 text-sunshine-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h4 className="text-sm font-bold text-sunshine-800">Remember to check in</h4>
                <p className="text-sm text-sunshine-700 mt-1">
                  Check-in opens 10 minutes before your session starts. You can check in from "My Reservations" or at the gym.
                </p>
              </div>
            </div>
          </div>

          {/* Confirmation Options */}
          <div>
            <h4 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-teal-500 rounded"></span>
              Get confirmation reminders (optional)
            </h4>
            <div className="space-y-3">
              <label className="flex items-center cursor-pointer touch-manipulation">
                <input
                  type="checkbox"
                  checked={emailConfirmation}
                  onChange={(e) => setEmailConfirmation(e.target.checked)}
                  className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-slate-400 rounded"
                />
                <span className="ml-3 text-sm text-slate-700 font-medium">
                  Email confirmation and reminder
                  {user?.email && (
                    <span className="text-slate-500 ml-1">
                      ({user.email})
                    </span>
                  )}
                </span>
              </label>

              <label className="flex items-center cursor-pointer touch-manipulation">
                <input
                  type="checkbox"
                  checked={textConfirmation}
                  onChange={(e) => handleTextConfirmationChange(e.target.checked)}
                  className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-slate-400 rounded"
                />
                <span className="ml-3 text-sm text-slate-700 font-medium">
                  Text message reminder (30 min before)
                </span>
              </label>

              {/* Phone Number Input - shows when text confirmation is checked OR phone exists */}
              {(textConfirmation || phoneNumber || isEditingPhone) && (
                <div className="ml-7 space-y-2">
                  <input
                    type="tel"
                    value={isEditingPhone ? tempPhoneNumber : phoneNumber}
                    onChange={handlePhoneChange}
                    onFocus={() => {
                      if (!isEditingPhone) {
                        setIsEditingPhone(true);
                        setTempPhoneNumber(phoneNumber);
                      }
                    }}
                    placeholder="(555) 123-4567"
                    className="w-full px-3 py-2 border-2 border-slate-900 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    maxLength={14}
                  />
                  {phoneSaveError && (
                    <p className="text-xs text-coral-600 font-medium">{phoneSaveError}</p>
                  )}
                  {isEditingPhone && (
                    <label className="flex items-center cursor-pointer touch-manipulation">
                      <input
                        type="checkbox"
                        checked={savePhoneToProfile}
                        onChange={(e) => setSavePhoneToProfile(e.target.checked)}
                        className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-slate-400 rounded"
                      />
                      <span className="ml-2 text-xs text-slate-700 font-medium">
                        Save to profile for future reservation confirmations
                      </span>
                    </label>
                  )}
                </div>
              )}
            </div>

            {(emailConfirmation || textConfirmation) && (
              <div className="mt-3 text-xs text-slate-500">
                {emailConfirmation && textConfirmation && 'Email and text confirmations will be sent.'}
                {emailConfirmation && !textConfirmation && 'Email confirmation will be sent.'}
                {!emailConfirmation && textConfirmation && 'Text confirmation will be sent.'}
                {' '}You can update your contact info in{' '}
                <a href="/settings" className="underline hover:text-slate-700 font-medium">
                  Settings
                </a>.
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ReservationSuccessModal;